import { reactive, toRefs } from 'vue'
import { ElMessage } from 'element-plus'
import type {
  PageResponse,
  Pageable,
  TableQueryContext,
  TableRequest,
  TableRequestAdapter,
  TableResponseAdapter
} from '../interface'
import type { SearchTriggerMode } from '@/components/CSearch/index.vue'

export interface UseTableOptions<T = any> {
  request?: TableRequest
  requestAdapter?: TableRequestAdapter
  responseAdapter?: TableResponseAdapter<T>
  initParam?: Record<string, any>
  pagination?: boolean
  initQueryEnable?: boolean
  searchTriggerMode?: SearchTriggerMode
  preserveResponseOrder?: boolean
  onQuery?: (context: TableQueryContext) => void
}

interface TableSearchOptions {
  silent?: boolean
  patch?: {
    rowKey: string
    fields: readonly string[]
  }
}

export const defaultTableRequestAdapter: TableRequestAdapter = context => ({
  ...(context.pagination ? { pageNo: context.pageNo, pageSize: context.pageSize } : {}),
  param: { ...context.initParam, ...context.searchParam }
})

export function defaultTableResponseAdapter<T>(response: any, context: TableQueryContext): PageResponse<T> {
  if (Array.isArray(response)) {
    return {
      list: response,
      pageNo: context.pageNo,
      pageSize: context.pageSize,
      totalCount: response.length
    }
  }
  return {
    list: response?.list ?? [],
    pageNo: response?.pageNo ?? context.pageNo,
    pageSize: response?.pageSize ?? context.pageSize,
    totalCount: response?.totalCount ?? 0
  }
}

const CREATE_TIME_KEYS = ['createTime', 'create_time', 'createdAt', 'created_at'] as const

/**
 * 统一管理端列表的创建时间倒序展示。
 * 服务端仍负责跨页排序；这里同时覆盖本地数据与未分页列表。
 */
function sortByCreateTimeDesc<T>(list: T[]): T[] {
  if (list.length < 2) return list
  const key = CREATE_TIME_KEYS.find(candidate =>
    list.some(item => item != null && typeof item === 'object' && candidate in (item as Record<string, unknown>))
  )
  if (!key) return list
  return list
    .map((item, index) => ({ item, index }))
    .sort((left, right) => {
      const leftValue = (left.item as Record<string, unknown> | null)?.[key]
      const rightValue = (right.item as Record<string, unknown> | null)?.[key]
      const leftTime = leftValue == null ? Number.NEGATIVE_INFINITY : Date.parse(String(leftValue))
      const rightTime = rightValue == null ? Number.NEGATIVE_INFINITY : Date.parse(String(rightValue))
      const normalizedLeft = Number.isNaN(leftTime) ? Number.NEGATIVE_INFINITY : leftTime
      const normalizedRight = Number.isNaN(rightTime) ? Number.NEGATIVE_INFINITY : rightTime
      return normalizedRight - normalizedLeft || left.index - right.index
    })
    .map(entry => entry.item)
}

export function useTable<T = any>(options: UseTableOptions<T> = {}) {
  const paginationEnabled = options.pagination ?? true
  const searchTriggerMode = options.searchTriggerMode ?? 'button'
  let allowNextQuery = options.initQueryEnable ?? true
  let currentInitParam = { ...(options.initParam ?? {}) }
  const state = reactive({
    loading: false,
    tableData: [] as T[],
    pageable: { pageNo: 1, pageSize: 10, totalCount: 0 } as Pageable,
    searchParam: {} as Record<string, any>,
    searchInitParam: {} as Record<string, any>,
    firstSearchInitParam: {} as Record<string, any>
  })

  function createQueryContext(): TableQueryContext {
    return {
      pageNo: state.pageable.pageNo,
      pageSize: state.pageable.pageSize,
      pagination: paginationEnabled,
      searchParam: Object.fromEntries(
        Object.entries(state.searchParam).filter(([, value]) => value !== null && value !== undefined && value !== '')
      ),
      initParam: { ...currentInitParam }
    }
  }

  function applyResponse(response: PageResponse<T> | T[], context: TableQueryContext) {
    const normalized = Array.isArray(response) ? defaultTableResponseAdapter<T>(response, context) : response
    const records = normalized.list ?? []
    state.tableData = (options.preserveResponseOrder ? records : sortByCreateTimeDesc(records)) as typeof state.tableData
    if (!paginationEnabled) return
    Object.assign(state.pageable, {
      pageNo: normalized.pageNo ?? context.pageNo,
      pageSize: normalized.pageSize ?? context.pageSize,
      totalCount: normalized.totalCount ?? 0
    })
  }

  function patchResponseFields(response: PageResponse<T> | T[], context: TableQueryContext, options: TableSearchOptions) {
    const patch = options.patch
    if (!patch) {
      applyResponse(response, context)
      return
    }
    const normalized = Array.isArray(response) ? defaultTableResponseAdapter<T>(response, context) : response
    const latestByKey = new Map<string, Record<string, unknown>>()
    for (const item of normalized.list ?? []) {
      if (item == null || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const key = record[patch.rowKey]
      if (key != null) latestByKey.set(String(key), record)
    }
    for (const item of state.tableData) {
      if (item == null || typeof item !== 'object') continue
      const record = item as Record<string, unknown>
      const key = record[patch.rowKey]
      if (key == null) continue
      const latest = latestByKey.get(String(key))
      if (!latest) continue
      for (const field of patch.fields) {
        if (Object.prototype.hasOwnProperty.call(latest, field)) record[field] = latest[field]
      }
    }
  }

  async function search(searchOptions: TableSearchOptions = {}) {
    if (!allowNextQuery) {
      allowNextQuery = true
      return
    }
    const context = createQueryContext()
    options.onQuery?.(context)
    if (!options.request) return context

    if (!searchOptions.silent) state.loading = true
    try {
      const params = (options.requestAdapter ?? defaultTableRequestAdapter)(context)
      const rawResponse = await options.request(params, context)
      const response = options.responseAdapter
        ? options.responseAdapter(rawResponse, context)
        : defaultTableResponseAdapter<T>(rawResponse, context)
      patchResponseFields(response, context, searchOptions)
      return rawResponse
    } catch (error) {
      if (!searchOptions.silent) {
        ElMessage.error(error instanceof Error && error.message ? error.message : '列表加载失败，请稍后重试')
      }
      return undefined
    } finally {
      if (!searchOptions.silent) state.loading = false
    }
  }

  function initPageNoSearch() {
    state.pageable.pageNo = 1
    return search()
  }

  function reset(first = false, triggerQuery = true) {
    state.searchParam = {
      ...(first ? state.firstSearchInitParam : state.searchInitParam)
    }
    if (triggerQuery && searchTriggerMode !== 'button') return initPageNoSearch()
    state.pageable.pageNo = 1
  }

  function handleSizeChange(size: number) {
    state.pageable.pageSize = size
    return initPageNoSearch()
  }

  function handleCurrentChange(pageNo: number) {
    state.pageable.pageNo = pageNo
    return search()
  }

  function setInitParam(value: Record<string, any>) {
    currentInitParam = { ...value }
  }

  function setControlledData(data: T[], totalCount = data.length) {
    state.tableData = (options.preserveResponseOrder ? data : sortByCreateTimeDesc(data)) as typeof state.tableData
    state.pageable.totalCount = totalCount
  }

  function setPage(page: Partial<Pageable>) {
    Object.assign(state.pageable, page)
  }

  return {
    ...toRefs(state),
    createQueryContext,
    search,
    initPageNoSearch,
    reset: (triggerQuery = true) => reset(false, triggerQuery),
    firstReset: (triggerQuery = true) => reset(true, triggerQuery),
    handleSizeChange,
    handleCurrentChange,
    setInitParam,
    setControlledData,
    setPage
  }
}
